import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, NgModule } from '@angular/core';
import { tap, finalize } from 'rxjs/operators';
import { CommonModule } from '@angular/common';

var LoggingLevel;
(function (LoggingLevel) {
    LoggingLevel[LoggingLevel["Debug"] = 0] = "Debug";
    LoggingLevel[LoggingLevel["Info"] = 1] = "Info";
    LoggingLevel[LoggingLevel["Warning"] = 2] = "Warning";
    LoggingLevel[LoggingLevel["Error"] = 3] = "Error";
})(LoggingLevel || (LoggingLevel = {}));

const LOGGING_CONFIG = new InjectionToken('Logging Config');

class LoggingService {
    constructor(config) {
        this.config = config;
    }
    debug(origin, message, additionalMessage) {
        this.log(LoggingLevel.Debug, { origin, message, additionalMessage });
    }
    info(origin, message, additionalMessage) {
        this.log(LoggingLevel.Info, { origin, message, additionalMessage });
    }
    warning(origin, message, additionalMessage) {
        this.log(LoggingLevel.Warning, { origin, message, additionalMessage });
    }
    error(origin, message, additionalMessage) {
        this.log(LoggingLevel.Error, { origin, message, additionalMessage });
    }
    log(level, logging) {
        const { origin, message, additionalMessage } = logging;
        if (this.config?.silent || (this.config?.level && this.config.level > level)) {
            return;
        }
        let consoleMessage = `%c${origin}: (${new Date().toLocaleTimeString()})%c ${message}`;
        consoleMessage += additionalMessage ? `: (${additionalMessage})` : '';
        console.log(consoleMessage, this.levelStyling(level), '');
    }
    levelStyling(level) {
        const defaults = 'padding: 2px 6px; border-radius: 4px; margin-bottom: 6px;';
        switch (level) {
            case LoggingLevel.Error:
                return `${defaults} background: #AF2C2B; color: white;`;
            case LoggingLevel.Warning:
                return `${defaults} background: #FFC453; color: black;`;
            case LoggingLevel.Info:
                return `${defaults} background: #3B8524; color: white;`;
            case LoggingLevel.Debug:
            default:
                return `${defaults} background: #0565C2; color: white;`;
        }
    }
}
LoggingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LoggingService, deps: [{ token: LOGGING_CONFIG, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
LoggingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LoggingService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LoggingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [LOGGING_CONFIG]
                }] }]; } });

class LoggingInterceptor {
    constructor(loggingService) {
        this.loggingService = loggingService;
    }
    intercept(request, next) {
        this.loggingService.debug('LoggingInterceptor', `${request.url}: Start`);
        let error;
        return next.handle(request).pipe(tap(() => (error = undefined), (requestError) => (error = requestError)), finalize(() => {
            error
                ? this.loggingService.error('LoggingInterceptor', `${request.url}: Failed`, error.message)
                : this.loggingService.debug('LoggingInterceptor', `${request.url}: Success`);
        }));
    }
}
LoggingInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LoggingInterceptor, deps: [{ token: LoggingService }], target: i0.ɵɵFactoryTarget.Injectable });
LoggingInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LoggingInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LoggingInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: LoggingService }]; } });

class DgxLoggingModule {
    static forRoot(config) {
        return {
            ngModule: DgxLoggingModule,
            providers: [{ provide: LOGGING_CONFIG, useValue: config }],
        };
    }
}
DgxLoggingModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DgxLoggingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DgxLoggingModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: DgxLoggingModule, imports: [CommonModule] });
DgxLoggingModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DgxLoggingModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DgxLoggingModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }] });

const mockLoggingConfig = {
    level: LoggingLevel.Debug,
    silent: false,
};

/**
 * Generated bundle index. Do not edit.
 */

export { DgxLoggingModule, LOGGING_CONFIG, LoggingInterceptor, LoggingLevel, LoggingService, mockLoggingConfig };
